import { Schema } from "effect"
import { AllowRule } from "./allow"
import { AgentKind, Effort, Owned } from "./common"
import {
  ItemId,
  Millis,
  NonNegInt,
  PosInt,
  ProfileId,
  ProjectId,
  RepoId,
  RunnerId,
  Sha256,
  UserId,
} from "./ids"
import { Labels, Os, ToolCap, Trust } from "./placement"
import { ProfileContainer } from "./resources"

// #region runner
/** Readiness check per profile (Paperclip's testEnvironment), shown on the Runners screen. */
export const ProfileReadiness = Schema.TaggedUnion({
  ready: {},
  logged_out: {},
  cli_missing: {},
  cli_too_old: { found: Schema.String, minimum: Schema.String },
  quota_exhausted: { resetsAt: Schema.NullOr(Millis) },
  error: { message: Schema.String },
  /** enabled = false or capacity 0 in runner.toml (console): advertised, never offered; maxConcurrent stays ≥ 1. */
  disabled: {},
})

/** Remaining quota as the CLI reports it (Claude rate_limit_event, Codex account/rateLimits). Never a credential. */
export const QuotaWindow = Schema.Struct({
  /** Native limit identifier; retains distinct account/model pools with the same duration. */
  id: Schema.optionalKey(Schema.String),
  /** A native quota alias or model family; absent means account-wide. */
  model: Schema.optionalKey(Schema.String),
  modelTag: Schema.optionalKey(Schema.String),
  window: Schema.Literals(["5h", "24h", "7d", "other"]),
  usedPercent: Schema.Number,
  resetsAt: Schema.NullOr(Millis),
})

/** Claude's native per-model weekly windows, shared by readiness and placement metadata. */
export const quotaModelTagOf = (id: string): string | undefined =>
  id === "seven_day_opus" ? "opus" : id === "seven_day_sonnet" ? "sonnet" : undefined

/** Claude Agent SDK `PermissionMode` (0.3.295; `default` | `acceptEdits` | `bypassPermissions` | `plan` | `dontAsk` | `auto`). */
export const ClaudePermissionMode = Schema.Literals([
  "default",
  "acceptEdits",
  "bypassPermissions",
  "plan",
  "dontAsk",
  "auto",
])
/** Codex app-server v2 `SandboxMode`. */
export const CodexSandboxMode = Schema.Literals(["read-only", "workspace-write", "danger-full-access"])
/** Codex app-server v2 `AskForApproval` (the string forms; `granular` is not offered). */
export const CodexApprovalPolicy = Schema.Literals(["untrusted", "on-request", "never"])

/**
 * Codex app-server v2 `ApprovalsReviewer` as Minga passes it (decision 0053): `auto_review` is Codex's own "Approve for
 * me" reviewer subagent; `user` routes every approval prompt to the client (a Needs-you card). `guardian_subagent` is
 * not offered.
 */
export const CodexApprovalsReviewer = Schema.Literals(["user", "auto_review"])

export type ClaudePermissionMode = typeof ClaudePermissionMode.Type
export type CodexSandboxMode = typeof CodexSandboxMode.Type
export type CodexApprovalPolicy = typeof CodexApprovalPolicy.Type
export type CodexApprovalsReviewer = typeof CodexApprovalsReviewer.Type

/** Decision 0020: the access mode of a runner profile, mirroring each CLI's own choices. Default "auto". */
export const AccessMode = Schema.Literals(["auto", "ask", "full"])
export type AccessMode = typeof AccessMode.Type

/**
 * What each access mode passes to each CLI (pure data, shared by runner and web; decision 0020's table). Codex Auto is
 * Codex's own "Approve for me": workspace-write + on-request + the `auto_review` reviewer (decision 0053).
 */
export const ACCESS_MODE_PRESETS = {
  auto: {
    claudePermissionMode: "auto",
    codexSandbox: "workspace-write",
    codexApprovalPolicy: "on-request",
    codexApprovalsReviewer: "auto_review",
  },
  ask: {
    claudePermissionMode: "default",
    codexSandbox: "read-only",
    codexApprovalPolicy: "on-request",
    codexApprovalsReviewer: "user",
  },
  full: {
    claudePermissionMode: "bypassPermissions",
    codexSandbox: "danger-full-access",
    codexApprovalPolicy: "never",
    codexApprovalsReviewer: "user",
  },
} as const satisfies Record<
  AccessMode,
  {
    claudePermissionMode: ClaudePermissionMode
    codexSandbox: CodexSandboxMode
    codexApprovalPolicy: CodexApprovalPolicy
    codexApprovalsReviewer: CodexApprovalsReviewer
  }
>

/** MCP servers a run loads: the CLI's own plus Minga's (default "cli"), or Minga's only (runner.toml escape hatch). */
export const RunMcpMode = Schema.Literals(["cli", "minga_only"])
export type RunMcpMode = typeof RunMcpMode.Type

/**
 * Under Auto: the user's MCP servers and connectors run without asking ("approve", default) or follow the CLI's own
 * approval rules ("ask"). Ask and Full ignore it (decision 0024).
 */
export const McpApproval = Schema.Literals(["approve", "ask"])
export type McpApproval = typeof McpApproval.Type

/**
 * A per-run request that can only narrow the profile (decision 0026). This wave: only the MCP server set. Absent = as
 * the profile says. `"minga_only"` narrows; `"cli"` is neutral (kept only in `Mind.runner.access`, where it records
 * that a person switched outside tools on). New fields need a new `RUNNER_FEATURES` entry (settings wave §4.4).
 */
export const AccessRequest = Schema.Struct({
  mcp: Schema.optionalKey(RunMcpMode),
  /** Deliberately constrain native tools for a planning/research-only run. The signed job enforces it. */
  permission: Schema.optionalKey(Schema.Literals(["read_only", "workspace_write"])),
})
export type AccessRequest = typeof AccessRequest.Type

/**
 * The signed job's access (`JobSpec.access`): the narrowing request plus, for a runner with the manifest feature
 * `access_allow`, the effective sandbox allowlist (workspace ∪ project, decision 0096). The allowlist only widens what
 * the CLI may do without asking and is passed as its native config; it never narrows and needs no feature to place a
 * run (a runner without `access_allow` gets no list and runs as before).
 */
export const JobAccess = Schema.Struct({
  ...AccessRequest.fields,
  allow: Schema.optionalKey(Schema.Array(AllowRule)),
})
export type JobAccess = typeof JobAccess.Type

/** A model id as a profile override or a preset names it. */
const ModelOverride = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(128))

/**
 * A runner profile's CLI permission settings, as configured in the runner console and `runner.toml` (decision 0020,
 * fix wave 2026-10-09 §4.1). `accessMode` picks the preset; the three mode-driven fields are **advanced overrides**:
 * null = take it from the access mode (`ACCESS_MODE_PRESETS`). `claudeSandbox` keeps its meaning (null = pass
 * nothing). Use `effectiveCli` for what a run actually passes. Minga adds no wrapper on top.
 */
export const ProfileCliSettings = Schema.Struct({
  /** Claude `permissionMode` override. */
  claudePermissionMode: Schema.NullOr(ClaudePermissionMode),
  /** Claude Code sandbox on/off (SDK `sandbox.enabled`); null = pass nothing. */
  claudeSandbox: Schema.NullOr(Schema.Boolean),
  /** Codex `sandbox` override. */
  codexSandbox: Schema.NullOr(CodexSandboxMode),
  /** Codex `approvalPolicy` override. */
  codexApprovalPolicy: Schema.NullOr(CodexApprovalPolicy),
  /** Absent only from a runner that predates decision 0020; the runner always fills it. */
  accessMode: Schema.optionalKey(AccessMode),
  /** Absent = "cli". */
  mcp: Schema.optionalKey(RunMcpMode),
  /** Settings wave (decision 0024): Auto only. Absent = "approve" (runners before 0.3.0). */
  mcpApproval: Schema.optionalKey(McpApproval),
  /** Codex, Auto only: commands can reach the network. Absent = true. */
  autoNetwork: Schema.optionalKey(Schema.Boolean),
  /** Minga's per-profile model for runs whose job names none. null/absent = the CLI default (console `defaultModel`). */
  modelOverride: Schema.optionalKey(Schema.NullOr(ModelOverride)),
  /** Effort for runs whose job names none. null/absent = the CLI default. */
  effortOverride: Schema.optionalKey(Schema.NullOr(Effort)),
})
export type ProfileCliSettings = typeof ProfileCliSettings.Type

/**
 * The console's wish for `ProfileCliSettings`: per field, absent = leave as configured on the runner, null = unset
 * (back to the access mode's value), a value = set it. `accessMode` has no null: set "auto" to go back to the default.
 */
export const ProfileCliWish = Schema.Struct({
  claudePermissionMode: Schema.optionalKey(Schema.NullOr(ClaudePermissionMode)),
  claudeSandbox: Schema.optionalKey(Schema.NullOr(Schema.Boolean)),
  codexSandbox: Schema.optionalKey(Schema.NullOr(CodexSandboxMode)),
  codexApprovalPolicy: Schema.optionalKey(Schema.NullOr(CodexApprovalPolicy)),
  accessMode: Schema.optionalKey(AccessMode),
  /** Settings wave (runner 0.3.0, feature `cli_settings_v2`): absent = leave; null clears where nullable. */
  mcp: Schema.optionalKey(RunMcpMode),
  mcpApproval: Schema.optionalKey(McpApproval),
  autoNetwork: Schema.optionalKey(Schema.Boolean),
  modelOverride: Schema.optionalKey(Schema.NullOr(ModelOverride)),
  effortOverride: Schema.optionalKey(Schema.NullOr(Effort)),
})
export type ProfileCliWish = typeof ProfileCliWish.Type

/** The wish keys only a runner with the feature `cli_settings_v2` (0.3.0) understands (H18 refuses them otherwise). */
export const CLI_SETTINGS_V2_KEYS = [
  "mcp",
  "mcpApproval",
  "autoNetwork",
  "modelOverride",
  "effortOverride",
] as const

/** What a run passes to its CLI, computed the same way by runner and web. */
export interface EffectiveCli {
  readonly accessMode: AccessMode
  readonly claudePermissionMode: ClaudePermissionMode
  readonly claudeSandbox: boolean | null
  readonly codexSandbox: CodexSandboxMode
  readonly codexApprovalPolicy: CodexApprovalPolicy
  /**
   * Codex `approvalsReviewer` (decision 0053): the preset's (`auto_review` under Auto) only when the approval policy is
   * `on-request` and MCP approval is `approve`; else `user` (a person who chose to be asked about MCP tools keeps
   * getting cards for everything; under `never` nothing is reviewed).
   */
  readonly codexApprovalsReviewer: CodexApprovalsReviewer
  readonly mcp: RunMcpMode
  /** Any of the three mode-driven fields is overridden. */
  readonly custom: boolean
  /** Under Auto: MCP tools run without asking ("approve") or as the CLI asks. Ask and Full ignore it. */
  readonly mcpApproval: McpApproval
  /**
   * Codex `sandbox_workspace_write.network_access` when the sandbox is `workspace-write`; null where it does not apply
   * (read-only and danger-full-access sandboxes, and Claude, whose caller ignores it).
   */
  readonly network: boolean | null
  readonly modelOverride: string | null
  readonly effortOverride: Effort | null
}

/** Facts only the runner knows (its profile's `config.toml`), as it reports them in `ConsoleCliSettings.effective`. */
export interface RunnerAccessFacts {
  readonly network?: boolean
  readonly mcpApproval?: McpApproval
  /** User MCP servers whose own config sets their approval mode (left untouched by Auto). */
  readonly ownApproval?: ReadonlyArray<string>
  /** Runner 0.6.1 (decision 0053): the reviewer its Codex runs pass. Facts without it = an older runner = `user`. */
  readonly codexApprovalsReviewer?: CodexApprovalsReviewer
}

/** Each mode-driven field = its override when set, else `ACCESS_MODE_PRESETS[accessMode ?? "auto"]`. */
export const effectiveCli = (c: ProfileCliSettings): EffectiveCli => {
  const accessMode = c.accessMode ?? "auto"
  const preset = ACCESS_MODE_PRESETS[accessMode]
  const codexSandbox = c.codexSandbox ?? preset.codexSandbox
  const codexApprovalPolicy = c.codexApprovalPolicy ?? preset.codexApprovalPolicy
  return {
    accessMode,
    claudePermissionMode: c.claudePermissionMode ?? preset.claudePermissionMode,
    claudeSandbox: c.claudeSandbox,
    codexSandbox,
    codexApprovalPolicy,
    codexApprovalsReviewer:
      codexApprovalPolicy === "on-request" && (c.mcpApproval ?? "approve") === "approve"
        ? preset.codexApprovalsReviewer
        : "user",
    mcp: c.mcp ?? "cli",
    custom: c.claudePermissionMode !== null || c.codexSandbox !== null || c.codexApprovalPolicy !== null,
    mcpApproval: c.mcpApproval ?? "approve",
    // network is Minga's to switch only in Auto (decision 0024); other modes keep the CLI's default (off)
    network: codexSandbox === "workspace-write" ? accessMode === "auto" && (c.autoNetwork ?? true) : null,
    modelOverride: c.modelOverride ?? null,
    effortOverride: c.effortOverride ?? null,
  }
}

/**
 * `effectiveCli` plus a run's request and the runner's facts (decision 0026). Pure. The runner calls it with its own
 * facts; the web with the facts from the console manifest's `effective`. Without facts, network and MCP approval
 * follow the settings. A request only narrows: `mcp: "minga_only"` turns a `cli` profile into Minga's servers only.
 */
export const effectiveAccess = (
  c: ProfileCliSettings,
  r: AccessRequest | undefined,
  facts?: RunnerAccessFacts,
): EffectiveCli => {
  const e = effectiveCli(c)
  return {
    ...e,
    mcp: r?.mcp === "minga_only" ? "minga_only" : e.mcp,
    mcpApproval: facts?.mcpApproval ?? e.mcpApproval,
    network: e.network === null ? null : (facts?.network ?? e.network),
    // A runner that reports facts without the key is older than 0.6.1 and sends no reviewer (decision 0053). With facts,
    // auto-review needs both the runner's report and these settings (a pending switch to Ask never shows it).
    codexApprovalsReviewer: !facts
      ? e.codexApprovalsReviewer
      : facts.codexApprovalsReviewer === "auto_review" && e.codexApprovalsReviewer === "auto_review"
        ? "auto_review"
        : "user",
  }
}

/** A run's request narrows something (the only case `Run.access` / `JobTemplate.access` / `JobSpec.access` carry it). */
export const narrowsAccess = (r: AccessRequest | undefined | null): r is AccessRequest =>
  r?.mcp === "minga_only" || r?.permission === "read_only"

/**
 * Manifest features and the runner version that introduced each (settings wave §5.2). `item_model` (decision 0039):
 * the runner reads v1 and v2 frames and item ids; the item-model cloud offers every run only to runners with it.
 * `checkouts` (decision 0047): the runner finds the clones you worked in with Claude or Codex and serves a single
 * match by itself (console repos carry `found`, the console part `ambiguousCheckouts`); the web uses it only to pick
 * the "no clone" hint. `artifact_upload` (decision 0050): the runner uploads `post_artifact{path}` and fetches
 * `ContextPack.attachments`; the cloud offers `path` only to runs on such a runner. `pr_watch` (decision 0059): the
 * runner watches the PRs the hub lists (`HubPush.WatchPrs`), reports them with `ReportPrs`, and executes signed merge
 * orders (`HubPush.Merge`); the hub pushes neither to a runner without it.
 */
export const RUNNER_FEATURES = {
  access_request: "0.3.0",
  cli_settings_v2: "0.3.0",
  item_model: "0.4.0",
  checkouts: "0.5.0",
  artifact_upload: "0.6.0",
  pr_watch: "0.7.0",
  /**
   * Wave E (decision 0063): `gh_login` = the console names the machine's `gh` login (`console.github`, never a token);
   * `rekey` = the runner takes a new key over the socket (`HubPush.Rekey` → `RekeyApplied`). The hub gates sharing a
   * machine on `rekey`, never on the version.
   */
  gh_login: "0.8.0",
  rekey: "0.8.0",
  subscription_connect: "0.9.1",
  /** Decision 0096: the runner maps `JobSpec.access.allow` to native CLI config; the hub sends the list only then. */
  access_allow: "0.9.8",
  /**
   * Decision 0138: the runner decodes and applies `mcpTool` allow rules (the hub leaves them out of older runners'
   * jobs), answers MCP tool prompts they cover without a card, offers them on "Always allow" (only to a cloud whose
   * Register reply lists `owner_grants`), adds `AUTO_DEFAULT_ALLOW` under Auto, and folds repeated reviewer refusals of
   * one command prefix in a run into one card.
   */
  owner_grants: "0.9.21",
  /**
   * Wave 3 (lessons 1–4): the runner enforces `JobSpec.limits.idleMs` and may end a run `no_progress`. The hub sets the
   * limit only for runners with this feature, so an older cloud or runner never sees the stop reason.
   */
  no_progress: "0.9.21",
  /**
   * Decision 0141: the runner fetches the run's secrets at start (`JobSpec.secrets.url`, authenticated with the run's
   * stream token), sets them in the CLI's environment, writes file secrets 0600 into a private per-run directory and
   * redacts every value from what it streams. The hub sends `secrets` only to such a runner, and places a run that
   * names secrets (`requires.secrets`) only there or on the Platform Runner (which gets them from the Worker).
   */
  secrets: "0.9.22",
  /**
   * Decision 0143: the runner reports its errors, versions, run trouble and crashes (`RunnerTelemetryReport`) to a cloud
   * whose Register reply lists `runner_telemetry`. Ships with the next CLI release (not in 0.9.21).
   */
  telemetry: "0.9.22",
  /**
   * Decision 0145: the runner reports the context of each run's main-loop model calls (`Usage.calls`: count, first,
   * last, largest) on the primary usage row. Additive: an older cloud drops the key. Ships with the next CLI release.
   */
  usage_calls: "0.9.23",
  /**
   * Decision 0149: the runner samples its machine (local: the whole machine; a Platform sandbox: its container) and
   * each run's process tree, sends heartbeat `load` and Final `resources` to a cloud whose Register reply lists
   * `resources`, and tells each run what machine it is on. The cloud reads load only from such runners (an older one is
   * "load not reported").
   */
  resources: "0.9.23",
  /**
   * Decision 0150: a Platform task sandbox fences at the hub's lease expiry minus PLATFORM_FENCE_MARGIN_MS
   * (`PlatformExecutionTick.leaseRemainingMs`), not 15 s after its last tick. The Worker sets a sandbox's own deadline
   * the same way only for an image with this feature.
   */
  platform_fence_at_expiry: "0.9.24",
  /**
   * Decision 0150: a Platform sandbox that heard no tick until its fence (its own, or the Worker's `/task/cancel` with
   * `reason: tick_lost`) ends `runner_lost` with error code `platform_tick_lost` instead of `interrupted`/`lease_lost`
   * or `runner_lost`/`runner_shutdown`. The cloud understands the code from 0150 on; an older image keeps its codes.
   */
  platform_tick_lost: "0.9.24",
  /**
   * Decision 0150: the runner serves the `fetch_artifact` tool locally (it downloads the artifact into the run's
   * working directory through the run's file route). The cloud lists the tool only to runs on such a runner.
   */
  artifact_fetch: "0.9.24",
  /**
   * Decision 0150: the runner places `ContextPack.inputs` (an item's linked input artifacts) into the worker's
   * workspace under `.minga/inputs/<ref>/<name>` at run start and lists them in the run's brief. The cloud sends
   * `inputs` only to such a runner.
   */
  inputs_materialized: "0.9.24",
  /**
   * Decision 0155: a Platform task sandbox started with `MINGA_PLATFORM_TOOL_NICE` (the Worker sets it) runs every
   * process but its own task server at that nice value (the CLI, its tools and tests), so a saturated sandbox still
   * answers its ticks, and reports its own side of each tick (`PlatformExecutionState.tick`). An older image ignores
   * the variable; the Worker reads the report only when it is there.
   */
  platform_tool_priority: "0.9.25",
} as const
export type RunnerFeature = keyof typeof RUNNER_FEATURES

const versionParts = (v: string): number[] =>
  (v.split(/[-+]/)[0] ?? "").split(".").map((x) => (Number.isFinite(Number(x)) ? Number(x) : 0))
/**
 * Decision 0150: whether a runner of `version` has `feature` (by the version that introduced it). For a Platform task
 * sandbox, whose image reports its runner version (`PlatformRuntime.minga`) but no manifest. An unknown version has none.
 */
export const runnerVersionHas = (version: string | null | undefined, feature: RunnerFeature): boolean => {
  if (!version) return false
  const a = versionParts(version),
    b = versionParts(RUNNER_FEATURES[feature])
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0)
    if (d !== 0) return d > 0
  }
  return true
}

/**
 * One CLI login on the machine, with its own home (CODEX_HOME / CLAUDE_CONFIG_DIR). This user's runner:
 * codex-personal (~/.codex, personal workspaces) and codex-every (~/.codex-alt, the Every workspace only).
 * A manifest sent to workspace W lists ONLY the profiles whose local scope includes W.
 */
export const RunnerProfile = Schema.Struct({
  id: ProfileId,
  agent: AgentKind,
  /** Display only ("~/.codex-alt"). The cloud never sends a path back down; jobs name the profile id. */
  homeDisplay: Schema.String,
  cliVersion: Schema.NullOr(Schema.String),
  /**
   * How the profile's CLI login pays. "unknown" (Bedrock, a failed probe) is sent only to a cloud with the `usage_v2`
   * feature (decision 0062); an older cloud would refuse the profile, so the runner folds it into "subscription" there.
   */
  auth: Schema.Literals(["subscription", "api_key", "unknown"]),
  /** Project-level scoping inside this workspace; null = every project. */
  projects: Schema.NullOr(Schema.Array(ProjectId)),
  /** Rate limits apply per account, not per machine. */
  maxConcurrent: PosInt,
  readiness: ProfileReadiness,
  quota: Schema.Array(QuotaWindow),
  /** Opaque provider/account identity from a native subscription probe; never a token or raw email. */
  quotaAccountKey: Schema.optionalKey(Schema.String),
  /** Hosted profiles point to their shared saved subscription; native profiles omit this. */
  subscriptionId: Schema.optionalKey(ProfileId),
  /** When the quota observation was sampled, distinct from the runner heartbeat. */
  quotaObservedAt: Schema.optionalKey(Millis),
  models: Schema.NullOr(Schema.Array(Schema.String)),
  defaultModel: Schema.optionalKey(Schema.String),
  modelOverride: Schema.optionalKey(Schema.NullOr(Schema.String)),
  /**
   * Decision 0149, Platform Runner profiles only (the cloud writes it into its own manifest; a runner never sends it):
   * the task sandbox's instance size and price per hour.
   */
  container: Schema.optionalKey(ProfileContainer),
})

export const RunnerRepo = Schema.Struct({
  repoId: RepoId,
  remote: Schema.String, // must match the workspace Repo.remote
  defaultBranch: Schema.String,
  /**
   * D7 (decision 0039): the wire name `taskId` stays this release; it holds an item id. A runner reports non-`tsk_`
   * worktrees only to a cloud whose Register reply advertises `item_model`.
   */
  worktrees: Schema.Array(Schema.Struct({ taskId: ItemId, branch: Schema.String, dirty: Schema.Boolean })),
})

/** Summary + hash of the on-device policy ceiling (only the owner, on the device, can raise it). */
export const PolicyCeilingSummary = Schema.Struct({
  hash: Sha256,
  allowedRepos: Schema.Array(RepoId),
  allowedAgents: Schema.Array(AgentKind),
  maxPermission: Schema.Literals(["read_only", "workspace_write", "workspace_write_network", "full_auto"]),
  allowComputerUse: Schema.Boolean,
  allowRunKinds: Schema.Array(Schema.Literals(["turn", "worker", "validator", "publish"])),
})

/** Sent with Register and again whenever it changes; the hub ignores older revisions. */
export const RunnerManifest = Schema.Struct({
  revision: NonNegInt,
  name: Schema.String, // "jannik-mbp"
  mode: Schema.Literals(["service", "one_job"]), // one_job = Cloudflare Sandbox, bound to one run
  trust: Trust,
  platform: Schema.Struct({ os: Os, osVersion: Schema.String, arch: Schema.Literals(["arm64", "x64"]) }),
  versions: Schema.Struct({ minga: Schema.String, bun: Schema.String, protocol: Schema.Int }),
  capacity: PosInt, // machine slots; each run costs 1 in v1
  profiles: Schema.Array(RunnerProfile),
  tools: Schema.Array(ToolCap),
  repos: Schema.Array(RunnerRepo),
  labels: Labels,
  ceiling: PolicyCeilingSummary,
  /** From 0.3.0: what this runner understands (`RUNNER_FEATURES` keys: "access_request", …, "checkouts"). */
  features: Schema.optionalKey(Schema.Array(Schema.String)),
  /** The runner-console v2 manifest (decision 0010). Display only; decoded leniently by the hub's sanitizeManifest. */
  console: Schema.optionalKey(Schema.Json),
})

export const RunnerAdminState = Schema.Literals(["active", "paused", "draining", "revoked"])
/** Derived, never stored: online (connected, fresh heartbeat, free slots) · busy · away (disconnected < 24 h) ·
 * offline · needs_attention (a profile logged out / CLI too old / runner version below minimum). */
export const RunnerPresence = Schema.Literals(["online", "busy", "away", "offline", "needs_attention"])

/**
 * Workspace DO record. A runner identity is one pairing of a machine with ONE workspace; a machine that serves
 * several workspaces holds one control socket (and one RunnerId) per workspace and enforces machine-wide
 * capacity locally. ownerId = owner lock: it only claims runs whose principal is its owner.
 */
/**
 * Wave E (decision 0063): whose work a runner takes. `owner` (Just me, the default) = only runs whose principal is its
 * owner, plus items its owner lent it to; `team` (The team) = any member's work. Cloud-only: the runner never sees it.
 */
export const RunnerServes = Schema.Literals(["owner", "team"])
export type RunnerServes = typeof RunnerServes.Type

/**
 * Decision 0128 (0126 §5): whose work a subscription (a runner profile) runs. `personal` (the default) = only its
 * owner's; `team` = anyone's in the workspace. Set by the subscription's owner. Cloud-only, like `RunnerServes`: the
 * cloud picks the profile per job (`JobSpec.agent.profileId`) and the runner never sees it.
 */
export const ProfileScope = Schema.Literals(["personal", "team", "projects"])
export type ProfileScope = typeof ProfileScope.Type

/**
 * Decision 0166 §3.1: the projects a `projects`-scoped subscription runs others' work in (projects its owner is a member
 * of). At most 200; ignored for `personal` and `team` (`team` = every project of the workspace).
 */
export const ProfileProjectIds = Schema.Array(ProjectId).check(Schema.isMaxLength(200))

/**
 * Decision 0128: a profile's owner (the person whose login or subscription it is) and its scope. Decision 0166 §3.1:
 * `projects` with `projectIds` shares it in those projects only.
 */
export const ProfileAccess = Schema.Struct({
  ownerId: UserId,
  scope: ProfileScope,
  projectIds: Schema.optionalKey(ProfileProjectIds),
})
export type ProfileAccess = typeof ProfileAccess.Type

/**
 * Decision 0168: a login on someone else's personal machine that its owner shared (with the workspace or some projects)
 * or that is the viewer's own, as that viewer's runner list carries it: which CLI and whether it is enabled and signed
 * in. No home, path, plan or account. The rest of such a machine stays redacted.
 */
export const RunnerSharedProfile = Schema.Struct({
  id: Schema.String,
  agent: AgentKind,
  ready: Schema.Boolean,
})
export type RunnerSharedProfile = typeof RunnerSharedProfile.Type

/** Decision 0166 §3.3: an owner-set name of a subscription or login ("Max · team", "Jannik's Pro"). */
export const SubscriptionLabel = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(60))

/** Wave E: one CLI's login kind on a runner, from its profiles (never a token or an e-mail address). */
export const RunnerCliLogin = Schema.Struct({
  plan: Schema.NullOr(Schema.String),
  kind: Schema.Literals(["subscription", "api_key", "unknown"]),
})
export type RunnerCliLogin = typeof RunnerCliLogin.Type

/**
 * Wave E: the logins line of a machine ("GitHub @jannikjung · Claude Max · Codex Plus"), shown before sharing it and on
 * a lend row. `known: false` = the runner is older than `gh_login` and the line can't be trusted to be complete.
 */
export const RunnerLogins = Schema.Struct({
  github: Schema.NullOr(
    Schema.Struct({ login: Schema.String.check(Schema.isMaxLength(39)), host: Schema.String }),
  ),
  claude: Schema.NullOr(RunnerCliLogin),
  codex: Schema.NullOr(RunnerCliLogin),
  known: Schema.Boolean,
})
export type RunnerLogins = typeof RunnerLogins.Type

export const RunnerRecord = Schema.Struct({
  id: RunnerId,
  ...Owned,
  name: Schema.String,
  admin: RunnerAdminState,
  tokenHash: Sha256, // sha256 of the runner token from device pairing; the token is never stored
  manifest: Schema.NullOr(RunnerManifest),
  incarnation: NonNegInt, // +1 on every Register; offers and claims carry it
  connectedAt: Schema.NullOr(Millis),
  lastHeartbeatAt: Schema.NullOr(Millis),
  createdAt: Millis,
  /** Wave E (decision 0063): absent = `owner` (records from before E). */
  serves: Schema.optionalKey(RunnerServes),
  /**
   * Decision 0128: the profiles whose owner or scope was set explicitly (every Platform Runner subscription; a local
   * profile once its owner chose Personal or Team). A profile without an entry is its runner owner's login, used as
   * wave E did (`serves` decides). Cloud-only.
   */
  profileAccess: Schema.optionalKey(Schema.Record(ProfileId, ProfileAccess)),
})
export type RunnerRecord = typeof RunnerRecord.Type
// #endregion runner

// #region console
/** Runner console manifest parts shared by cloud, runner and web (moved from the cloud's provisional copy, settings
 * wave §5.2). Display only; the hub's sanitizer rebuilds them from whatever a runner sent. */
const Text = (max: number) => Schema.String.check(Schema.isMaxLength(max))

/** One model a profile's CLI offers (probed). `supportsAutoMode` (Claude, 0.3.0): false = Auto falls back (S5). */
export const ConsoleModelInfo = Schema.Struct({
  id: Text(128),
  label: Schema.optionalKey(Text(128)),
  isDefault: Schema.optionalKey(Schema.Boolean),
  reasoningEfforts: Schema.optionalKey(Schema.Array(Text(32))),
  contextWindow: Schema.optionalKey(Schema.Number),
  supportsAutoMode: Schema.optionalKey(Schema.Boolean),
})
export type ConsoleModelInfo = typeof ConsoleModelInfo.Type

/**
 * A profile's CLI settings as the console manifest reports them: the configured values (`ProfileCliSettings`) plus
 * what runs pass (`effective`, as the runner resolved them, including `config.toml` facts) and whether any override
 * is set (`custom`). 0.3.0 always sends every key. The console `RunnerProfile.defaultModel` (what the CLI itself would
 * use) is a different field from `modelOverride` (Minga's per-profile choice).
 */
export const ConsoleCliSettings = Schema.Struct({
  ...ProfileCliSettings.fields,
  effective: Schema.optionalKey(
    Schema.Struct({
      claudePermissionMode: ClaudePermissionMode,
      codexSandbox: CodexSandboxMode,
      codexApprovalPolicy: CodexApprovalPolicy,
      mcpApproval: Schema.optionalKey(McpApproval),
      network: Schema.optionalKey(Schema.Boolean),
      ownApproval: Schema.optionalKey(Schema.Array(Text(128))),
      /** Runner 0.6.1, Codex profiles (decision 0053): the reviewer runs pass. Absent = an older runner (`user`). */
      codexApprovalsReviewer: Schema.optionalKey(CodexApprovalsReviewer),
    }),
  ),
  custom: Schema.optionalKey(Schema.Boolean),
})
export type ConsoleCliSettings = typeof ConsoleCliSettings.Type

/** The runner facts inside a console `effective` (the input `effectiveAccess` takes). */
export const accessFactsOf = (c: ConsoleCliSettings | undefined): RunnerAccessFacts | undefined => {
  const e = c?.effective
  if (!e) return undefined
  return {
    ...(e.network !== undefined ? { network: e.network } : {}),
    ...(e.mcpApproval !== undefined ? { mcpApproval: e.mcpApproval } : {}),
    ...(e.ownApproval !== undefined ? { ownApproval: e.ownApproval } : {}),
    ...(e.codexApprovalsReviewer !== undefined ? { codexApprovalsReviewer: e.codexApprovalsReviewer } : {}),
  }
}
// #endregion console

export const UsageWindow = Schema.Struct({
  id: Schema.String,
  /** Exact quota alias reported by Codex when this limit is model-specific. */
  model: Schema.optionalKey(Schema.String),
  label: Schema.String,
  /** 0-100 */
  usedPercent: Schema.Number,
  /** ISO 8601 */
  resetsAt: Schema.optionalKey(Schema.String),
})
export type UsageWindow = typeof UsageWindow.Type

export const ProfileUsage = Schema.Struct({
  /** Where the numbers come from, e.g. `codex:account/rateLimits/read`, `claude:usage`, `claude:rate_limit_event`. */
  source: Schema.String,
  windows: Schema.Array(UsageWindow),
  updatedAt: Schema.String,
})
export type ProfileUsage = typeof ProfileUsage.Type
